import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  updateTranscriberProgramFaxList,
  updateTranscriberProgramIndexOnList,
  resetTranscriberProgramError,
} from "./TranscriberProgramList.actions";
import {
  createDefaultListState,
  resolveTranscriberProgramCode,
} from "./TranscriberProgramList.helpers";
import withRouter from "utils/withRouter";
import ListHeaderBackButton from "components/ListHeaderBackButton";
import Container from "react-bootstrap/Container";
import {
  buildTranscriberProgramListColumns,
  getTranscriberProgramConfig,
} from "lookup/TranscriberProgramConstants.jsx";
import { formatDateForDisplay } from "services/MomentUtils";
import { showNotification } from "services/NotificationService";
import ServerSideReactTable from "commons/table/ServerSideReactTable";

const TRANSCRIBER_PROGRAM_TYPE_LIST_PATH = "/transcription/program-type-list";

const Tip = (props) => (
  <div style={{ textAlign: "center", color: "#888", fontSize: "0.9em" }}>
    <em>{props.children}</em>
  </div>
);

const TranscriberProgramList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const programCode = resolveTranscriberProgramCode(location);
  const programConfig = getTranscriberProgramConfig(programCode);

  const reduxProgramState = useSelector(
    (state) => state.transcriberProgramList?.programs?.[programCode]
  );

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [pages, setPages] = useState(0);
  const [totalElements, setTotalElements] = useState("Loading ...");
  const [listState, setListState] = useState(() => createDefaultListState(programCode));

  const updateData = useCallback(
    (state, preventUpdate) => {
      if (!preventUpdate) {
        if (state.sorted !== programConfig.defaultSorting) {
          state.sorted.forEach((element) => {
            element.isDefault = false;
          });
        }

        dispatch(
          updateTranscriberProgramFaxList(
            programCode,
            state.pageSize,
            state.page,
            state.sorted,
            state.filtered,
            state.resized,
            state.expanded
          )
        );
      } else {
        setListState(state);
      }
    },
    [dispatch, programCode, programConfig.defaultSorting]
  );

  const onUpdateTable = useCallback(
    (filterValues, sortBy, pageSize, pageNo) => {
      const newListState = {
        ...listState,
        pageSize,
        page: pageNo,
        sorted: sortBy ? sortBy : [],
        filtered: filterValues,
      };
      updateData(newListState);
    },
    [listState, updateData]
  );

  useEffect(() => {
    const nextListState =
      reduxProgramState?.listState ?? createDefaultListState(programCode);
    setListState(nextListState);
    dispatch(
      updateTranscriberProgramFaxList(
        programCode,
        nextListState.pageSize,
        nextListState.page,
        nextListState.sorted,
        nextListState.filtered,
        nextListState.resized,
        nextListState.expanded
      )
    );
  }, [programCode, dispatch]);

  useEffect(() => {
    if (!reduxProgramState) {
      return;
    }

    setData(reduxProgramState.data ?? []);
    setPages(reduxProgramState.pages ?? 0);
    setTotalElements(
      reduxProgramState.totalElements !== undefined
        ? reduxProgramState.totalElements
        : "Loading ..."
    );
    setLoading(Boolean(reduxProgramState.loading));
    if (reduxProgramState.listState) {
      setListState(reduxProgramState.listState);
    }
  }, [reduxProgramState]);

  useEffect(() => {
    if (!reduxProgramState?.isError) {
      return;
    }

    showNotification({
      title: "Error Loading Data!",
      message: "You have entered invalid information.",
      position: "bl",
      type: "error",
    });
    dispatch(resetTranscriberProgramError(programCode));
  }, [reduxProgramState?.isError, dispatch, programCode]);

  const renderDateCell = (date) => (
    <span style={{ width: "100%", display: "flex", justifyContent: "center" }}>
      {date}
    </span>
  );

  const renderEffectiveDateCol = (date) => {
    if (date) {
      return renderDateCell(formatDateForDisplay(date));
    }
    return <span className="center">N/A</span>;
  };

  const renderLastModifiedDateCol = (date) => {
    if (date) {
      return renderDateCell(formatDateForDisplay(date));
    }
    return <span className="center">N/A</span>;
  };

  const columns = useMemo(
    () =>
      buildTranscriberProgramListColumns(programConfig, {
        renderLastModifiedDateCol,
        renderEffectiveDateCol,
      }),
    [programConfig]
  );

  const handleRowClick = useCallback(
    (rowInfo) => {
      if (rowInfo === undefined) {
        return;
      }

      if (programConfig.trackIndexOnRowClick) {
        dispatch(updateTranscriberProgramIndexOnList(programCode, rowInfo.index));
      }

      navigate(programConfig.getDetailsPath(rowInfo.original));
    },
    [dispatch, navigate, programCode, programConfig]
  );

  return (
    <Container className="list-container" fluid>
      <div className="fax-program-list-panel">
        <div className="codex-version-display-bar fax-program-list-panel__header">
          <ListHeaderBackButton
            onClick={() => navigate(TRANSCRIBER_PROGRAM_TYPE_LIST_PATH)}
            ariaLabel="Back to Transcriber Fax Programs"
          />
          <span className="list-header-meta">
            Total Transcribed Faxes: <strong>{totalElements}</strong>
          </span>
          <strong className="list-header-title">{programConfig.title}</strong>
        </div>
        <div className="fax-program-list-panel__body">
          <ServerSideReactTable
            columnDefination={columns}
            data={data}
            currentPage={listState.page}
            sorted={listState.sorted}
            filters={listState.filtered}
            totalPages={pages}
            totalRecords={totalElements === "Loading ..." ? 0 : totalElements}
            loading={loading}
            enableMultiRowSelection={false}
            addRowSelection={false}
            defaultPageSize={listState.pageSize}
            pageSizeOptions={[5, 10, 15, 20, 30, 50]}
            onUpdateTableData={onUpdateTable}
            onRowClick={handleRowClick}
          />
          <Tip>Tip: Hold shift when sorting to multi-sort and disable sort!</Tip>
        </div>
      </div>
    </Container>
  );
};

export default withRouter(TranscriberProgramList);
